import { Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import Slider from "react-rangeslider";
import { QuestionType } from "src/common/types";

const useStyles = makeStyles((theme: any) => ({
  container: {
    "& .rangeslider": {
      "&:before": {
        background: theme.palette.primary.main,
        content: '""',
        display: "block" as "block",
        height: 6,
        left: 12,
        opacity: 0.5,
        position: "absolute" as "absolute",
        right: 12,
        top: 0,
        [theme.breakpoints.up("md")]: {
          left: 7,
          right: 7
        }
      },
      margin: 0
    },
    "& .rangeslider-horizontal": {
      background: "transparent",
      borderRadius: 0,
      boxShadow: "none",
      height: "auto"
    },
    "& .rangeslider__fill": {
      background: "transparent",
      boxShadow: "none"
    },
    "& .rangeslider__handle": {
      "&:after": {
        content: "none"
      },
      background: theme.palette.primary.main,
      border: "none",
      boxShadow: "none",
      height: 26,
      margin: "4px 0 0",
      top: 0,
      width: 26,
      zIndex: 2,
      [theme.breakpoints.up("md")]: {
        height: 16,
        width: 16
      },
      ["@media print"]: {
        height: 16,
        width: 16
      }
    },
    "& .rangeslider__label-item": {
      maxWidth: "20% !important",
      "&:before": {
        background: theme.palette.primary.main,
        content: '""',
        height: 14,
        left: "50%",
        marginLeft: -1.5,
        position: "absolute" as "absolute",
        top: -27.5,
        width: 3
      },
      "&:empty:before": {
        height: 6,
        marginLeft: -1,
        top: -23.5,
        width: 2
      },
      "&:first-child": {
        "&:before": {
          left: "auto" as "auto",
          margin: 0,
          right: 12
        },
        left: "auto !important" as "auto",
        right: 0,
        textAlign: "right" as "right",
        transform: "none",
        [theme.breakpoints.up("md")]: {
          "&:before": {
            right: 7
          }
        }
      },
      "&:last-child": {
        "&:before": {
          left: 12,
          margin: 0,
          right: "auto" as "auto"
        },
        left: "0 !important",
        textAlign: "left" as "left",
        transform: "none",
        [theme.breakpoints.up("md")]: {
          "&:before": {
            left: 7
          }
        }
      },
      color: "#4d4d4e",
      fontSize: "1.2rem",
      overflowWrap: "break-word" as "break-word",
      textAlign: "center" as "center",
      top: 0,
      [theme.breakpoints.down("sm")]: {
        fontSize: "1.4rem"
      }
    },
    "& .rangeslider__labels": {
      display: "inline-block" as "inline-block",
      margin: "23.4px 0 0",
      padding: 0,
      width: "100%"
    },
    margin: "0 12px 12px",
    padding: "12px 0 0",
    printColorAdjust: "exact" as "exact",
    userSelect: "none" as "none"
  },
  currentValueLabel: {
    fontSize: "1.5rem",
    fontWeight: "bold" as "bold"
  },
  invalid: {
    background: "#ffebee"
  },
  noValue: {
    "& .rangeslider__handle": {
      cursor: "default" as "default",
      opacity: 0
    }
  },
  preview: {
    "& .rangeslider__handle": {
      cursor: "default" as "default"
    },
    "& .rangeslider__labels .rangeslider__label-item": {
      cursor: "default" as "default"
    }
  },
  readOnly: {
    "& .rangeslider__handle": {
      cursor: "default" as "default"
    },
    "& .rangeslider__labels .rangeslider__label-item": {
      cursor: "default" as "default"
    },
    "@media print": {
      opacity: 1
    },
    opacity: 0.45
  },
  textVariant: {
    "& $valueContainer": {
      "@media print": {
        display: "none" as "none"
      },
      display: "block" as "block",
      [theme.breakpoints.up("md")]: {
        display: "none" as "none"
      }
    },
    "& .rangeslider__label-item": {
      fontSize: 0,
      [theme.breakpoints.up("md")]: {
        "&:first-child": {
          fontSize: "1.4rem"
        },
        "&:last-child": {
          fontSize: "1.4rem"
        },
        fontSize: "1.4rem"
      },
      [theme.breakpoints.down("sm")]: {
        "&:first-child": {
          fontSize: "1.2rem",
          maxWidth: "40% !important"
        },
        "&:last-child": {
          fontSize: "1.2rem",
          maxWidth: "40% !important"
        }
      }
    }
  },
  valueContainer: {
    display: "none" as "none",
    lineHeight: "1.7rem",
    margin: "0 0 12px",
    minHeight: "1.7rem"
  },
  valueText: {
    color: "#4d4d4e",
    fontSize: "1.2rem",
    [theme.breakpoints.up("md")]: {
      fontSize: "1.4rem"
    }
  }
}));

const TextVariant = [QuestionType.TextScale, QuestionType.FinalRating, QuestionType.Scale];

interface IProps {
  onChange?: (newValue: number) => void;
  value: number | null;
  readOnly?: boolean;
  preview?: boolean;
  labels: {
    [s: string]: string;
  };
  invalid?: boolean;
  max: number;
  min: number;
  step: number;
  type: QuestionType.NumericScale | QuestionType.TextScale | QuestionType.FinalRating | QuestionType.Scale;
}

export const ScaleQuestion = ({
  onChange,
  value,
  readOnly = false,
  preview = false,
  invalid = false,
  labels,
  max,
  min,
  step,
  type
}: IProps) => {
  const classes = useStyles();
  const containerRef = React.useRef<HTMLDivElement>(null);
  const handleRef = React.useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;
  const handleContainerRef = React.useRef<HTMLDivElement>(null) as React.MutableRefObject<HTMLDivElement>;

  const handleChange = React.useCallback(
    (newValue: number) => {
      if (!readOnly && onChange) {
        onChange(newValue);
      }
    },
    [onChange]
  );

  // Calculate height of parent to fit labels
  const onResize = React.useCallback(() => {
    if (containerRef.current) {
      const container = containerRef.current.querySelector("ul") as HTMLUListElement;
      const elements: [HTMLLIElement] = Array.prototype.slice.call(containerRef.current.querySelectorAll("li"));
      const maxHeight = elements.reduce((acc, element): number => {
        const { height } = element.getBoundingClientRect();
        return height > acc ? height : acc;
      }, 0);
      container.style.minHeight = `${maxHeight}px`;
    }
  }, [containerRef]);

  const containerClasses = React.useMemo(() => {
    const classNames = [classes.container];

    if (TextVariant.includes(type)) {
      classNames.push(classes.textVariant);
    }

    if (value === null) {
      classNames.push(classes.noValue);
    }
    if (readOnly) {
      classNames.push(classes.readOnly);
    }
    if (invalid) {
      classNames.push(classes.invalid);
    }
    if (preview) {
      classNames.push(classes.preview);
    }

    return classNames.join(" ");
  }, [readOnly, value, invalid, preview]);

  // A reference to cache container element
  const labelsContainerRef = React.useRef<HTMLUListElement | null>(null);

  // For cancelling previously scheduled timeouts
  const getLabelsOffsetTimeout = React.useRef<NodeJS.Timer | null>(null);

  // Used to store an array of label offsets in percents
  const labelsOffsetRef = React.useRef<string[]>([]);

  /**
   * Converts label offset from `px` to percents
   * and saves it to `labelsOffsetRef` reference
   */
  const getLabelsOffset = React.useCallback(() => {
    // Cancel previous timeout, it might happend that there are multiple
    // at the same time
    if (getLabelsOffsetTimeout.current) {
      clearTimeout(getLabelsOffsetTimeout.current);
    }

    if (containerRef.current) {
      // get label elements of scale (li)
      const elements = containerRef.current.querySelectorAll("li");
      const { left } = elements[0].style;

      // if left of the first element is 0 it means that the Scale component
      // did not calculate the offset yet, scheduling another check in 200ms
      if (left === "0px") {
        getLabelsOffsetTimeout.current = setTimeout(getLabelsOffset, 200);
      } else if (left && left.includes("px")) {
        const { width: containerWidth } = (elements[0].parentElement as HTMLUListElement).getBoundingClientRect();

        // Assign offsets to `labelsOffsetRef` reference.
        labelsOffsetRef.current = (Array.prototype.slice.call(elements) as [HTMLLIElement]).map(element => {
          // Getting offset of the element in percents.
          const elemLeft = element.style.left as string;
          const intLeft = parseInt(elemLeft.replace("px", ""), 10);
          const offset = `${((intLeft / containerWidth) * 100).toFixed(8)}%`;
          return offset;
        });
      }
    } else {
      getLabelsOffsetTimeout.current = setTimeout(getLabelsOffset, 200);
    }
  }, []);

  /**
   * Applies the saved offset in reference to the DOM Element
   */
  const setLabelsOffset = React.useCallback(() => {
    if (!labelsContainerRef.current && containerRef.current) {
      labelsContainerRef.current = containerRef.current.querySelector("ul") as HTMLUListElement;
    }

    const labelsContainer = labelsContainerRef.current as HTMLUListElement;
    // NodeList to Array
    const elements: [HTMLLIElement] = Array.prototype.slice.call(labelsContainer.querySelectorAll("li"));

    elements.forEach((element, key) => {
      const elemLeft = labelsOffsetRef.current[key];
      if (elemLeft) {
        element.style.left = labelsOffsetRef.current[key];
      }
    });
  }, []);

  // Same as above, a reference to hold the offset of slide handle in percents
  const handleOffsetRef = React.useRef<string | null>(null);
  // For cancelling previous timeouts
  const getHandleTimeout = React.useRef<NodeJS.Timeout | null>(null);
  /**
   * Converts handle offset from `px` to percents
   * and saves it to `handleOffsetRef` reference
   */
  const getHandleOffset = React.useCallback(() => {
    // Cancel previous timeout, if exists
    if (getHandleTimeout.current) {
      clearTimeout(getHandleTimeout.current);
    }

    if (containerRef.current) {
      // Caching the found handle element
      if (!handleRef.current) {
        handleRef.current = containerRef.current.querySelector(".rangeslider__handle") as HTMLDivElement;
      }

      // Cache parent element
      if (!handleContainerRef.current) {
        handleContainerRef.current = (handleRef.current as HTMLDivElement).parentElement as HTMLDivElement;
      }

      const left = handleRef.current.style.left as string;
      // Same as above, if left offset is `0px` the Scale component
      // did not calculate yet the offset, scheduling another try in 200ms
      if (left === "0px") {
        getHandleTimeout.current = setTimeout(getHandleOffset, 200);
      } else if (left && left.includes("px")) {
        // Getting offset in percents
        const intLeft = parseInt(left.replace(`px`, ""), 10);
        const { width: containerWidth } = handleContainerRef.current.getBoundingClientRect();
        handleOffsetRef.current = `${((intLeft / containerWidth) * 100).toFixed(4)}%`;
      }
    }
  }, []);

  /**
   * Applies the saved offset in reference to the DOM Element
   */
  const setHandleOffset = React.useCallback(() => {
    if (containerRef.current) {
      if (!handleRef.current) {
        handleRef.current = containerRef.current.querySelector(".rangeslider__handle") as HTMLDivElement;
      }
      handleRef.current.style.left = handleOffsetRef.current as string;
    }
  }, []);

  React.useEffect(() => {
    // the scale values range can be null
    // if so, there is no need to calculate handle offset
    // since it's not displayed at all
    if (typeof value === "number") {
      getHandleOffset();
    }
  }, [value]);

  // A refrence to save the mediaQuery
  const mediaQueryRef = React.useRef<MediaQueryList | null>(null);

  // A callback to handle `@media print`
  const handleOnPrint = React.useCallback((mql: MediaQueryListEvent) => {
    if (mql.matches) {
      setLabelsOffset();
      setHandleOffset();
      onResize();
    }
  }, []);

  React.useEffect(() => {
    // Calculate labels offset
    getLabelsOffset();

    // attach mediaQuery to reference,
    if (!mediaQueryRef.current) {
      mediaQueryRef.current = window.matchMedia("print");
      mediaQueryRef.current.addListener(handleOnPrint);
    }

    // Cleanup
    return () => {
      if (mediaQueryRef.current) {
        mediaQueryRef.current.removeListener(handleOnPrint);
        mediaQueryRef.current = null;
      }
    };
  }, []);

  React.useEffect(() => {
    if (readOnly && value && containerRef.current) {
      const allNumeric = Object.entries(labels).every(([, labelValue]) => {
        return labelValue === "" || !isNaN(parseInt(labelValue, 10));
      });

      if (allNumeric) {
        const valueLabel = labels[value];
        containerRef.current.querySelectorAll("li").forEach(element => {
          if (element.innerText === valueLabel) {
            element.classList.add(classes.currentValueLabel);
          }
        });
      }
    }
  }, [readOnly, labels, value]);

  React.useEffect(() => {
    if (containerRef.current) {
      // Split width of container between labels
      const maxWidth = 100 / Object.entries(labels).filter(([key, optionValue]) => optionValue).length;
      const elements = containerRef.current.querySelectorAll("li");
      elements.forEach((element, index) => {
        const overlaps = index === 0 || index === elements.length - 1;
        element.style.maxWidth = `${overlaps ? maxWidth / 2 : maxWidth}%`;
      });
    }
    // Recalculate height on resize
    window.addEventListener("resize", onResize);
    onResize();
    return () => {
      window.removeEventListener("resize", onResize);
    };
  }, [labels, onResize]);

  return (
    <div ref={containerRef} className={containerClasses}>
      <div className={classes.valueContainer}>
        {value === null ? null : (
          <Typography className={classes.valueText} align="center">
            {labels[Math.round(value as number)]}
          </Typography>
        )}
      </div>
      <Slider
        onChange={handleChange}
        labels={labels || {}}
        value={value || 0}
        tooltip={false}
        min={min}
        max={max}
        step={step}
      />
    </div>
  );
};
